import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { updateWorkflowItem2MutationDocument } from '@liscio/api';
import { hasVisibleHtmlContent, useGraphQLClient } from '@liscio/common';

import {
  encodeFieldPrompt,
  type CustomFieldDefinition,
} from './useCreateCustomItem';

function buildFirmAuthoredContent(field: CustomFieldDefinition) {
  const raw = field.textContent ?? '';
  const visible = hasVisibleHtmlContent(raw);
  if (field.fieldType === 'text') {
    return {
      contentType: field.fieldType,
      textContent: visible ? raw : '',
    };
  }
  return {
    contentType: field.fieldType,
    ...(visible ? { textContent: raw } : {}),
  };
}

function primaryFirmFileRef(field: CustomFieldDefinition) {
  return field.firmFiles?.[0];
}

/**
 * Updates an existing firm custom workflow item via `updateWorkflowItem2`.
 */
export function useUpdateCustomItem() {
  const gqlClient = useGraphQLClient();
  const queryClient = useQueryClient();

  const { mutateAsync: updateMutation, isLoading: isUpdating } =
    gqlClient.useMutationGraphQL(updateWorkflowItem2MutationDocument, {
      onError: () => {
        toast.error('Failed to update custom item.');
      },
    });

  const updateCustomField = useCallback(
    async (workflowItemId: string, field: CustomFieldDefinition) => {
      const prompt = encodeFieldPrompt(field);
      const firmAuthoredContent = buildFirmAuthoredContent(field);
      const fileRef = primaryFirmFileRef(field);
      const documentId =
        field.fieldType === 'file' && fileRef?.id != null
          ? fileRef.id
          : undefined;

      // File custom steps often have uuid in preview (REST dependency) but no numeric id until
      // re-upload. Metadata-only edits (label, hint, contentType) do not require documentId;
      // omit the variable so the backend does not run document sync (see UpdateWorkflowItem2).

      try {
        const response = await updateMutation({
          workflowItemId,
          prompt,
          hint: null,
          firmAuthoredContent,
          ...(field.fieldType === 'file' && documentId != null
            ? { documentId }
            : {}),
        });

        if (response.updateWorkflowItem2?.errors?.length) {
          const errMsg = response.updateWorkflowItem2.errors[0].message;
          toast.error(errMsg || 'Could not update custom step.');
          return false;
        }

        queryClient.invalidateQueries(['workflowRequest']);
        queryClient.invalidateQueries(['items']);
        queryClient.invalidateQueries(['/api/v5/firms/requests']);
        toast.success('Custom step updated.');
        return true;
      } catch {
        return false;
      }
    },
    [updateMutation, queryClient]
  );

  return { updateCustomField, isUpdating };
}
